import React, { useState, useEffect, useRef } from "react";
import {
  RotateCcw,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  MapPin,
} from "lucide-react";

const SuperKnightExplorer = () => {
  const [boardRows, setBoardRows] = useState(8);
  const [boardCols, setBoardCols] = useState(8);
  const [knightPos, setKnightPos] = useState({ x: 3, y: 3 });
  const [startPos, setStartPos] = useState({ x: 3, y: 3 });
  const [targetPos, setTargetPos] = useState(null);
  const [visitedCells, setVisitedCells] = useState(new Set(["3,3"]));
  const [moveHistory, setMoveHistory] = useState([{ x: 3, y: 3, step: 0 }]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMoveHistory, setShowMoveHistory] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedMode, setSelectedMode] = useState("move");
  const [showReachability, setShowReachability] = useState(false);
  const [showHints, setShowHints] = useState(true);
  const [showTrajectory, setShowTrajectory] = useState(false);
  const [showNextStepExits, setShowNextStepExits] = useState(false);
  const [selectedColor, setSelectedColor] = useState("blue");
  const [cellColors, setCellColors] = useState(new Map());
  const boardRef = useRef(null);
  const [cellSize, setCellSize] = useState(64);

  // 预设的涂色颜色
  const colorPresets = [
    {
      name: "blue",
      label: "浅蓝",
      class: "bg-blue-100",
      border: "border-blue-300",
    },
    {
      name: "red",
      label: "浅红",
      class: "bg-red-100",
      border: "border-red-300",
    },
    {
      name: "green",
      label: "浅绿",
      class: "bg-green-100",
      border: "border-green-300",
    },
    {
      name: "yellow",
      label: "浅黄",
      class: "bg-yellow-100",
      border: "border-yellow-300",
    },
    {
      name: "purple",
      label: "浅紫",
      class: "bg-purple-100",
      border: "border-purple-300",
    },
    {
      name: "pink",
      label: "浅粉",
      class: "bg-pink-100",
      border: "border-pink-300",
    },
    { name: "clear", label: "清除", class: "", border: "" },
  ];

  // 超级马的8种可能移动
  const knightMoves = [
    { dx: 3, dy: 4 },
    { dx: 3, dy: -4 },
    { dx: -3, dy: 4 },
    { dx: -3, dy: -4 },
    { dx: 4, dy: 3 },
    { dx: 4, dy: -3 },
    { dx: -4, dy: 3 },
    { dx: -4, dy: -3 },
  ];

  // 响应式计算格子大小
  useEffect(() => {
    const updateCellSize = () => {
      if (boardRef.current) {
        const containerWidth = window.innerWidth - 32; // 减去padding
        const maxCellSize = 64;
        const minCellSize = 24;

        // 基于屏幕宽度和棋盘列数计算
        const calculatedSize = Math.floor(containerWidth / boardCols);
        const newSize = Math.max(
          minCellSize,
          Math.min(maxCellSize, calculatedSize)
        );

        setCellSize(newSize);
      }
    };

    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [boardCols]);

  // 检查位置是否在棋盘内
  const isValidPos = (x, y) =>
    x >= 0 && x < boardCols && y >= 0 && y < boardRows;

  // 获取从当前位置可以到达的所有位置
  const getValidMoves = (x, y) => {
    return knightMoves
      .map((move) => ({ x: x + move.dx, y: y + move.dy }))
      .filter(
        (pos) =>
          isValidPos(pos.x, pos.y) && !visitedCells.has(`${pos.x},${pos.y}`)
      );
  };

  // 检查是否满足目标条件
  const checkTargetCondition = (from, to) => {
    const dx = Math.abs(to.x - from.x);
    const dy = Math.abs(to.y - from.y);
    return (dx === 4 && dy === 3) || (dx === 3 && dy === 4);
  };

  // 获取满足条件的所有目标位置
  const getValidTargets = (from) => {
    const targets = [];
    for (let x = 0; x < boardCols; x++) {
      for (let y = 0; y < boardRows; y++) {
        if (checkTargetCondition(from, { x, y })) {
          targets.push({ x, y });
        }
      }
    }
    return targets;
  };

  // 获取任意位置的可达格子数（不考虑已访问）
  const getReachableCount = (x, y) => {
    return knightMoves
      .map((move) => ({ x: x + move.dx, y: y + move.dy }))
      .filter((pos) => isValidPos(pos.x, pos.y)).length;
  };

  // 计算从指定位置出发还能到达多少个未访问格子（剩余出口数）
  const getAvailableExitsCount = (x, y) => {
    return knightMoves
      .map((move) => ({ x: x + move.dx, y: y + move.dy }))
      .filter(
        (pos) =>
          isValidPos(pos.x, pos.y) && !visitedCells.has(`${pos.x},${pos.y}`)
      ).length;
  };

  // 获取可达性的颜色（更丰富的色彩差异）
  const getReachabilityColor = (count) => {
    const colors = {
      0: "bg-gray-200",
      1: "bg-red-200",
      2: "bg-orange-200",
      3: "bg-amber-200",
      4: "bg-emerald-200",
      5: "bg-lime-200",
      6: "bg-green-200",
      7: "bg-cyan-200",
      8: "bg-blue-200",
    };
    return colors[count] || "bg-gray-100";
  };

  // 获取从指定位置可以到达的所有位置（用于显示）
  const getPossibleMoves = (x, y) => {
    return knightMoves
      .map((move) => ({ x: x + move.dx, y: y + move.dy }))
      .filter((pos) => isValidPos(pos.x, pos.y));
  };

  // 检查某个位置是否是从选中格子可达的
  const isReachableFromSelected = (x, y) => {
    if (selectedMode === "start") {
      return getPossibleMoves(startPos.x, startPos.y).some(
        (move) => move.x === x && move.y === y
      );
    }
    return false;
  };

  // 更新棋盘大小
  const updateBoardSize = (newRows, newCols) => {
    setBoardRows(newRows);
    setBoardCols(newCols);

    const newStartX = Math.min(startPos.x, newCols - 1);
    const newStartY = Math.min(startPos.y, newRows - 1);
    const newStart = { x: newStartX, y: newStartY };

    setStartPos(newStart);
    setKnightPos(newStart);
    setTargetPos(null);
    setVisitedCells(new Set([`${newStartX},${newStartY}`]));
    setMoveHistory([{ ...newStart, step: 0 }]);
    setCurrentStep(0);
    setIsPlaying(false);
    setCellColors(new Map());
  };

  // 处理格子点击
  const handleCellClick = (x, y) => {
    if (selectedMode === "start") {
      setStartPos({ x, y });
      setKnightPos({ x, y });
      setVisitedCells(new Set([`${x},${y}`]));
      setMoveHistory([{ x, y, step: 0 }]);
      setCurrentStep(0);
      setTargetPos(null);
    } else if (selectedMode === "move") {
      const validMoves = getValidMoves(knightPos.x, knightPos.y);
      const isValidMove = validMoves.some(
        (move) => move.x === x && move.y === y
      );

      if (isValidMove) {
        const newPos = { x, y };
        setKnightPos(newPos);
        setVisitedCells((prev) => new Set([...prev, `${x},${y}`]));
        setMoveHistory((prev) => [
          ...prev.slice(0, currentStep + 1),
          { ...newPos, step: currentStep + 1 },
        ]);
        setCurrentStep((prev) => prev + 1);
      }
    } else if (selectedMode === "target") {
      setTargetPos({ x, y });
    } else if (selectedMode === "color") {
      const cellKey = `${x},${y}`;
      setCellColors((prev) => {
        const newColors = new Map(prev);
        if (selectedColor === "clear") {
          newColors.delete(cellKey);
        } else {
          newColors.set(cellKey, selectedColor);
        }
        return newColors;
      });
    }
  };

  // 重置游戏
  const reset = () => {
    setKnightPos(startPos);
    setTargetPos(null);
    setVisitedCells(new Set([`${startPos.x},${startPos.y}`]));
    setMoveHistory([{ ...startPos, step: 0 }]);
    setCurrentStep(0);
    setIsPlaying(false);
    setCellColors(new Map());
  };

  // 回放功能
  const goToStep = (step) => {
    if (step >= 0 && step < moveHistory.length) {
      setCurrentStep(step);
      setKnightPos(moveHistory[step]);
      const visited = new Set();
      for (let i = 0; i <= step; i++) {
        visited.add(`${moveHistory[i].x},${moveHistory[i].y}`);
      }
      setVisitedCells(visited);
    }
  };

  // 自动播放
  useEffect(() => {
    if (isPlaying && currentStep < moveHistory.length - 1) {
      const timer = setTimeout(() => {
        goToStep(currentStep + 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isPlaying && currentStep >= moveHistory.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentStep, moveHistory.length]);

  // 获取格子的颜色类
  const getCellClass = (x, y) => {
    const isKnight = knightPos.x === x && knightPos.y === y;
    const isStart = startPos.x === x && startPos.y === y;
    const isVisited = visitedCells.has(`${x},${y}`);
    const isValidMove = getValidMoves(knightPos.x, knightPos.y).some(
      (move) => move.x === x && move.y === y
    );
    const isTarget = targetPos && targetPos.x === x && targetPos.y === y;
    const isValidTarget = getValidTargets(startPos).some(
      (t) => t.x === x && t.y === y
    );
    const isHovered = hoveredCell && hoveredCell.x === x && hoveredCell.y === y;
    const isReachable = isReachableFromSelected(x, y);

    let classes =
      "border flex items-center justify-center relative cursor-pointer transition-all ";

    // 检查是否有自定义颜色
    const cellKey = `${x},${y}`;
    const customColor = cellColors.get(cellKey);

    // 可达性分析模式优先
    if (showReachability) {
      const count = getReachableCount(x, y);
      const colorClass = getReachabilityColor(count);
      classes += `${colorClass} text-gray-700 `;
    } else if (customColor) {
      const colorPreset = colorPresets.find((c) => c.name === customColor);
      if (colorPreset) {
        classes += `${colorPreset.class} `;
      }
    } else {
      if ((x + y) % 2 === 0) {
        classes += "bg-white ";
      } else {
        classes += "bg-gray-100 ";
      }
    }

    classes += "border-gray-300 ";

    if (isKnight && !isStart) {
      classes += "ring-2 ring-blue-500 ring-inset bg-blue-50 ";
    } else if (isStart && isKnight) {
      classes += "ring-2 ring-purple-500 ring-inset bg-purple-50 ";
    } else if (isStart) {
      classes += "ring-2 ring-purple-400 ring-inset ";
    } else if (isTarget) {
      classes += "ring-2 ring-red-500 ring-inset bg-red-50 ";
    } else if (isValidMove && selectedMode === "move" && showHints) {
      classes += "ring-2 ring-green-500 ring-inset bg-green-50 animate-pulse ";
    } else if (isValidTarget && !targetPos && selectedMode === "target") {
      classes += "ring-1 ring-orange-400 ring-inset ";
    }

    if (selectedMode === "start" && isReachable && !isStart && showHints) {
      classes +=
        "ring-2 ring-purple-300 ring-inset bg-purple-100 animate-pulse ";
    }

    if (selectedMode === "color") {
      classes += "hover:opacity-75 ";
    }

    if (isVisited && !isKnight && !showReachability) {
      classes += "bg-gray-300 cursor-not-allowed ";
    }

    if (isHovered && !isVisited) {
      classes += "scale-105 z-10 shadow-lg ";
    }

    return classes;
  };

  // 获取格子中心坐标（相对于棋盘）
  const getCellCenter = (x, y) => {
    return {
      x: x * cellSize + cellSize / 2,
      y: (boardRows - 1 - y) * cellSize + cellSize / 2,
    };
  };

  // 生成轨迹路径
  const generateTrajectoryPath = () => {
    if (!showTrajectory || moveHistory.length <= 1) return null;

    const paths = [];
    for (let i = 0; i < moveHistory.length - 1; i++) {
      if (i >= currentStep) break;

      const from = getCellCenter(moveHistory[i].x, moveHistory[i].y);
      const to = getCellCenter(moveHistory[i + 1].x, moveHistory[i + 1].y);

      paths.push({
        from,
        to,
        step: i,
      });
    }
    return paths;
  };

  // 获取格子内容
  const getCellContent = (x, y) => {
    const isKnight = knightPos.x === x && knightPos.y === y;
    const isStart = startPos.x === x && startPos.y === y;
    const isTarget = targetPos && targetPos.x === x && targetPos.y === y;
    const stepNumber = moveHistory.findIndex(
      (move) => move.x === x && move.y === y
    );

    if (showReachability) {
      const count = getReachableCount(x, y);
      return (
        <>
          {getCellContentBase(x, y)}
          <div className="absolute bottom-0 right-0 text-xs font-bold text-gray-700 bg-white bg-opacity-70 px-1 rounded">
            {count}
          </div>
        </>
      );
    }

    return getCellContentBase(x, y);
  };

  // 基础格子内容
  const getCellContentBase = (x, y) => {
    const isKnight = knightPos.x === x && knightPos.y === y;
    const isStart = startPos.x === x && startPos.y === y;
    const isTarget = targetPos && targetPos.x === x && targetPos.y === y;
    const stepNumber = moveHistory.findIndex(
      (move) => move.x === x && move.y === y
    );
    const isValidMove = getValidMoves(knightPos.x, knightPos.y).some(
      (move) => move.x === x && move.y === y
    );

    // 根据格子大小调整内容尺寸
    const knightSize =
      cellSize < 32 ? "text-sm" : cellSize < 48 ? "text-lg" : "text-3xl";
    const targetSize =
      cellSize < 32 ? "text-sm" : cellSize < 48 ? "text-lg" : "text-3xl";
    const stepSize =
      cellSize < 32 ? "text-xs" : cellSize < 48 ? "text-sm" : "text-lg";
    const iconSize =
      cellSize < 32 ? "text-xs" : cellSize < 48 ? "text-sm" : "text-lg";
    const exitSize = Math.min(cellSize * 0.4, 32);

    if (isKnight && isStart) {
      return (
        <div className="relative">
          <div className={knightSize}>♞</div>
          <div className="absolute -top-1 -right-1 text-purple-600">
            <MapPin
              size={Math.min(
                iconSize === "text-xs" ? 8 : iconSize === "text-sm" ? 12 : 16,
                16
              )}
            />
          </div>
        </div>
      );
    } else if (isKnight) {
      return <div className={knightSize}>♞</div>;
    } else if (isStart) {
      return (
        <div className="text-purple-600">
          <MapPin
            size={
              iconSize === "text-xs" ? 12 : iconSize === "text-sm" ? 16 : 24
            }
          />
        </div>
      );
    } else if (isTarget) {
      return <div className={targetSize}>🎯</div>;
    } else if (stepNumber >= 0) {
      return (
        <div className={`${stepSize} font-bold text-gray-600`}>
          {stepNumber}
        </div>
      );
    } else if (
      isValidMove &&
      selectedMode === "move" &&
      showNextStepExits &&
      !showReachability
    ) {
      const exitsCount = getAvailableExitsCount(x, y);
      return (
        <div className="relative">
          <div
            className="bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-xs"
            style={{
              width: exitSize,
              height: exitSize,
              fontSize: cellSize < 32 ? "8px" : cellSize < 48 ? "10px" : "12px",
            }}
          >
            {exitsCount}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center p-2 sm:p-4 bg-gray-50 min-h-screen">
      <h1 className="text-xl sm:text-3xl font-bold mb-2 sm:mb-4 text-gray-800 text-center">
        超级马问题探索器
      </h1>

      {/* 控制面板 */}
      <div className="mb-2 sm:mb-4 flex gap-2 sm:gap-4 flex-wrap justify-center max-w-full">
        {/* 棋盘大小控制 */}
        <div className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300">
          <span className="text-xs sm:text-sm text-gray-700 font-medium whitespace-nowrap">
            棋盘:
          </span>
          <input
            type="number"
            min="4"
            max="15"
            value={boardRows}
            onChange={(e) =>
              updateBoardSize(parseInt(e.target.value) || 4, boardCols)
            }
            className="w-12 sm:w-16 px-1 sm:px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded"
          />
          <span className="text-xs sm:text-sm text-gray-500">×</span>
          <input
            type="number"
            min="4"
            max="15"
            value={boardCols}
            onChange={(e) =>
              updateBoardSize(boardRows, parseInt(e.target.value) || 4)
            }
            className="w-12 sm:w-16 px-1 sm:px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded"
          />
        </div>

        {/* 模式按钮 */}
        <div className="flex gap-1 sm:gap-2 flex-wrap">
          <button
            onClick={() => setSelectedMode("move")}
            className={`px-2 sm:px-4 py-1 sm:py-2 rounded flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm ${
              selectedMode === "move"
                ? "bg-blue-500 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            ♞ 移动
          </button>
          <button
            onClick={() => setSelectedMode("start")}
            className={`px-2 sm:px-4 py-1 sm:py-2 rounded flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm ${
              selectedMode === "start"
                ? "bg-purple-500 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            <MapPin size={12} /> 起点
          </button>
          <button
            onClick={() => setSelectedMode("target")}
            className={`px-2 sm:px-4 py-1 sm:py-2 rounded flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm ${
              selectedMode === "target"
                ? "bg-red-500 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            🎯 目标
          </button>
          <button
            onClick={() => setSelectedMode("color")}
            className={`px-2 sm:px-4 py-1 sm:py-2 rounded flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm ${
              selectedMode === "color"
                ? "bg-orange-500 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            🎨 涂色
          </button>
        </div>

        {/* 分析和重置按钮 */}
        <div className="flex gap-1 sm:gap-2">
          <button
            onClick={() => setShowReachability(!showReachability)}
            className={`px-2 sm:px-4 py-1 sm:py-2 rounded flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm ${
              showReachability
                ? "bg-indigo-500 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            📊 分析
          </button>

          <button
            onClick={reset}
            className="px-2 sm:px-4 py-1 sm:py-2 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm"
          >
            <RotateCcw size={12} /> 重置
          </button>
        </div>
      </div>

      {/* 涂色颜色选择器 */}
      {selectedMode === "color" && (
        <div className="mb-2 sm:mb-4 flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300 flex-wrap">
          <span className="text-xs sm:text-sm text-gray-700 font-medium whitespace-nowrap">
            颜色:
          </span>
          {colorPresets.map((color) => (
            <button
              key={color.name}
              onClick={() => setSelectedColor(color.name)}
              className={`w-6 h-6 sm:w-8 sm:h-8 rounded border-2 transition-all ${
                color.class || "bg-white"
              } ${
                selectedColor === color.name
                  ? "ring-2 ring-gray-400 ring-offset-1"
                  : "hover:scale-110"
              } ${color.border || "border-gray-300"}`}
              title={color.label}
            >
              {color.name === "clear" && <span className="text-xs">×</span>}
            </button>
          ))}
          <button
            onClick={() => setCellColors(new Map())}
            className="ml-1 sm:ml-2 px-2 sm:px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            清除
          </button>
        </div>
      )}

      {/* 功能开关 */}
      <div className="mb-2 sm:mb-4 flex gap-2 sm:gap-4 flex-wrap justify-center text-xs sm:text-sm">
        <label className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showHints}
            onChange={(e) => setShowHints(e.target.checked)}
            className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-gray-700 font-medium whitespace-nowrap">
            💡 提示
          </span>
        </label>

        <label className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showTrajectory}
            onChange={(e) => setShowTrajectory(e.target.checked)}
            className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-gray-700 font-medium whitespace-nowrap">
            📍 轨迹
          </span>
        </label>

        <label className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showNextStepExits}
            onChange={(e) => setShowNextStepExits(e.target.checked)}
            className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-gray-700 font-medium whitespace-nowrap">
            🔢 出口数
          </span>
        </label>

        <label className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showMoveHistory}
            onChange={(e) => setShowMoveHistory(e.target.checked)}
            className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-gray-700 font-medium whitespace-nowrap">
            📋 历史
          </span>
        </label>
      </div>

      {/* 提示信息 */}
      <div className="mb-2 sm:mb-4 text-center h-4 sm:h-6">
        {selectedMode === "move" && showHints && showNextStepExits && (
          <p className="text-green-600 font-medium text-xs sm:text-sm">
            点击绿色格子移动（圆圈数字为剩余出口数）
          </p>
        )}
        {selectedMode === "move" && showHints && !showNextStepExits && (
          <p className="text-green-600 font-medium text-xs sm:text-sm">
            点击绿色高亮格子移动超级马
          </p>
        )}
        {selectedMode === "start" && showHints && (
          <p className="text-purple-600 font-medium text-xs sm:text-sm">
            点击设置新起点（紫色为可达位置）
          </p>
        )}
        {selectedMode === "target" && (
          <p className="text-red-600 font-medium text-xs sm:text-sm">
            点击设置目标位置
          </p>
        )}
        {selectedMode === "color" && (
          <p className="text-orange-600 font-medium text-xs sm:text-sm">
            选择颜色后点击格子染色
          </p>
        )}
        {showReachability && (
          <p className="text-indigo-600 text-xs sm:text-sm">
            可达性数字显示在右下角
          </p>
        )}
      </div>

      {/* 棋盘 */}
      <div className="mb-2 sm:mb-4 relative overflow-auto max-w-full">
        <div
          ref={boardRef}
          className="grid gap-0 border-2 border-gray-400 bg-gray-400 relative mx-auto"
          style={{
            gridTemplateColumns: `repeat(${boardCols}, minmax(0, 1fr))`,
            width: boardCols * cellSize,
            height: boardRows * cellSize,
          }}
        >
          {Array(boardRows)
            .fill(0)
            .map((_, y) =>
              Array(boardCols)
                .fill(0)
                .map((_, x) => (
                  <div
                    key={`${x},${y}`}
                    className={getCellClass(x, boardRows - 1 - y)}
                    style={{ width: cellSize, height: cellSize }}
                    onClick={() => handleCellClick(x, boardRows - 1 - y)}
                    onMouseEnter={() =>
                      setHoveredCell({ x, y: boardRows - 1 - y })
                    }
                    onMouseLeave={() => setHoveredCell(null)}
                  >
                    {getCellContent(x, boardRows - 1 - y)}
                  </div>
                ))
            )}

          {/* 轨迹SVG叠加层 */}
          {showTrajectory && (
            <svg
              className="absolute inset-0 pointer-events-none"
              width={boardCols * cellSize}
              height={boardRows * cellSize}
              style={{ top: 0, left: 0 }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                  fill="#ea580c"
                >
                  <polygon points="0 0, 10 3.5, 0 7" />
                </marker>
              </defs>
              {generateTrajectoryPath()?.map((path, index) => (
                <line
                  key={index}
                  x1={path.from.x}
                  y1={path.from.y}
                  x2={path.to.x}
                  y2={path.to.y}
                  stroke="#ea580c"
                  strokeWidth={Math.max(2, cellSize / 20)}
                  markerEnd="url(#arrowhead)"
                  opacity="0.8"
                />
              ))}
            </svg>
          )}
        </div>

        {/* 坐标轴标签 */}
        <div className="flex mt-1 justify-center">
          <div className="flex" style={{ width: boardCols * cellSize }}>
            {Array(boardCols)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="text-center text-xs sm:text-sm text-gray-600"
                  style={{ width: cellSize }}
                >
                  {i + 1}
                </div>
              ))}
          </div>
        </div>
        <div className="absolute -left-4 sm:-left-6 top-0 flex flex-col">
          {Array(boardRows)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="flex items-center text-xs sm:text-sm text-gray-600"
                style={{ height: cellSize }}
              >
                {boardRows - i}
              </div>
            ))}
        </div>
      </div>

      {/* 回放控制 */}
      <div className="mb-2 sm:mb-4 flex items-center gap-2 sm:gap-4 bg-white p-2 sm:p-3 rounded-lg shadow-sm">
        <button
          onClick={() => goToStep(currentStep - 1)}
          disabled={currentStep === 0}
          className="p-1 sm:p-2 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={16} />
        </button>

        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="p-1 sm:p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>

        <button
          onClick={() => goToStep(currentStep + 1)}
          disabled={currentStep >= moveHistory.length - 1}
          className="p-1 sm:p-2 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={16} />
        </button>

        <span className="text-xs sm:text-sm text-gray-600 font-medium">
          {currentStep} / {moveHistory.length - 1}
        </span>
      </div>

      {/* 移动历史 */}
      {showMoveHistory && (
        <div className="max-w-full w-full max-w-2xl mb-2 sm:mb-4">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-bold mb-2 sm:mb-3 text-sm sm:text-lg text-gray-800">
              移动历史
            </h3>
            <div className="max-h-32 sm:max-h-40 overflow-y-auto">
              <div className="space-y-1 text-xs sm:text-sm text-gray-600">
                {moveHistory.map((move, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 flex-wrap"
                  >
                    <span className="font-medium text-gray-700 whitespace-nowrap">
                      步骤 {index}:
                    </span>
                    <span className="whitespace-nowrap">
                      ({move.x + 1}, {move.y + 1})
                    </span>
                    {index > 0 && (
                      <span className="text-gray-500 whitespace-nowrap">
                        [移动: ({move.x - moveHistory[index - 1].x},{" "}
                        {move.y - moveHistory[index - 1].y})]
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 简化的图例 - 移动端优化 */}
      <div className="mt-2 sm:mt-4 p-2 sm:p-3 bg-white rounded-lg shadow-sm text-xs sm:text-sm text-gray-600 border border-gray-200 max-w-full">
        <h4 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">图例</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1 sm:gap-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-50 ring-2 ring-blue-500 ring-inset rounded"></div>
            <span>超级马</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-white ring-2 ring-purple-400 ring-inset rounded"></div>
            <span>起点</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-red-50 ring-2 ring-red-500 ring-inset rounded"></div>
            <span>目标</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-green-50 ring-2 ring-green-500 ring-inset rounded animate-pulse"></div>
            <span>可移动{!showHints && "(隐藏)"}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-300 rounded flex items-center justify-center text-xs font-bold">
              3
            </div>
            <span>已访问</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-100 border border-gray-300 rounded"></div>
            <span>涂色</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
              5
            </div>
            <span>出口数{!showNextStepExits && "(隐藏)"}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-white border border-gray-300 rounded flex items-center justify-center relative">
              <svg width="16" height="12" className="absolute">
                <line
                  x1="2"
                  y1="6"
                  x2="14"
                  y2="6"
                  stroke="#ea580c"
                  strokeWidth="1"
                />
              </svg>
            </div>
            <span>轨迹{!showTrajectory && "(隐藏)"}</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1 sm:mt-2">
          💡 移动端支持触摸操作，点击格子进行游戏
        </p>
      </div>

      <footer className="mt-4 sm:mt-8 w-full text-center text-xs sm:text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default SuperKnightExplorer;
